import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "./lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0,
  maximumFractionDigits: 0 })}`;
const CAT = { part_number: "Part #", quantity: "Quantity", price: "Price", spec: "Spec",
  change_order: "Change order", freight: "Freight", duplicate: "Duplicate", coverage: "Coverage" };

/** PRC-REV — what the review keeps catching, and what slipped past it. */
export default function ReviewSection({ canEdit }) {
  const [m, setM] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => opsApi.reviewMetrics().then((d) => { setM(d); setCfg(d.settings); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the review metrics"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await opsApi.reviewSettingsPatch({ threshold: Number(cfg.threshold), daily_cap: Number(cfg.daily_cap),
        rate_per_min: Number(cfg.rate_per_min), enabled: !!cfg.enabled });
      toast.success("Purchasing review settings saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Those settings could not be saved"); }
    finally { setBusy(false); }
  };

  if (!m) {
    return <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading review metrics…
    </div>;
  }
  const c = m.coverage;
  return (
    <div className="space-y-4" data-testid="purchasing-review">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[["Sent with unacknowledged findings", `${m.sent_with_unacknowledged_findings.length}`,
            "prc-metric-unacked", m.sent_with_unacknowledged_findings.length ? "text-rose-700" : "text-emerald-700"],
          ["Review coverage", `${c.pct}%`, "prc-metric-coverage", "text-slate-900"],
          ["Reviewed / sent", `${c.reviewed} of ${c.sent} · ${c.skipped} skipped · ${c.unavailable} unavailable`,
            "prc-metric-counts", "text-slate-900"],
          ["Calls today", `${m.usage.calls} of ${m.usage.cap} · ${(m.usage.prompt_tokens + m.usage.completion_tokens).toLocaleString()} tokens`,
            "prc-metric-usage", "text-slate-900"]].map(([label, value, tid, tone]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={tid}>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
            <p className={`stat-num text-2xl font-semibold ${tone}`}>{value}</p>
          </div>
        ))}
      </div>

      {m.sent_with_unacknowledged_findings.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="prc-unacked-list">
          <p className="text-sm font-semibold text-rose-900">Sent with unacknowledged high findings — should be zero</p>
          <ul className="mt-1 space-y-0.5 text-xs text-rose-900">
            {m.sent_with_unacknowledged_findings.map((p) => (
              <li key={p.po_id}><span className="font-mono">{p.po_number}</span> · {p.manufacturer} · {p.job_number} — {p.project_name} · {p.high} high</li>
            ))}
          </ul>
        </div>
      )}

      {m.over_threshold_unreviewed.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="prc-unreviewed-list">
          <p className="text-sm font-semibold text-amber-900">
            Issued at or above {money(cfg.threshold)} without a completed review
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-amber-900">
            {m.over_threshold_unreviewed.map((p) => (
              <li key={p.po_id}><span className="font-mono">{p.po_number}</span> · {money(p.total)} · {p.job_number} — {p.project_name} · {p.reason}{p.acknowledged ? " · acknowledged" : ""}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="prc-by-category">
        <p className="px-4 pt-3 text-sm font-semibold text-slate-900">Findings by category, by month</p>
        <p className="px-4 pb-2 text-xs text-slate-500">What the review keeps catching.</p>
        {m.findings_by_category_month.length === 0 ? (
          <p className="px-4 pb-4 text-xs text-slate-500">No reviews have run yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr><th className="px-3 py-1.5 text-left">Month</th>
                {m.categories.map((k) => <th key={k} className="px-3 py-1.5 text-right">{CAT[k] || k}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {m.findings_by_category_month.map((row) => (
                <tr key={row.month} data-testid={`prc-month-${row.month}`}>
                  <td className="px-3 py-1.5 font-mono">{row.month}</td>
                  {m.categories.map((k) => <td key={k} className="px-3 py-1.5 text-right">{row[k] || "—"}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="prc-settings">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <Search className="h-4 w-4 text-[#007BFF]" /> Settings · Purchasing review
        </p>
        <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
          <label className="block"><span className="text-slate-500">Auto-review threshold ($)</span>
            <Input type="number" value={cfg.threshold} disabled={!canEdit} className="mt-1 h-8 text-xs"
                   onChange={(e) => setCfg({ ...cfg, threshold: e.target.value })} data-testid="prc-setting-threshold" /></label>
          <label className="block"><span className="text-slate-500">Daily call cap</span>
            <Input type="number" value={cfg.daily_cap} disabled={!canEdit} className="mt-1 h-8 text-xs"
                   onChange={(e) => setCfg({ ...cfg, daily_cap: e.target.value })} data-testid="prc-setting-cap" /></label>
          <label className="block"><span className="text-slate-500">Calls per minute</span>
            <Input type="number" value={cfg.rate_per_min} disabled={!canEdit} className="mt-1 h-8 text-xs"
                   onChange={(e) => setCfg({ ...cfg, rate_per_min: e.target.value })} data-testid="prc-setting-rate" /></label>
          <label className="mt-5 flex items-center gap-2"><input type="checkbox" checked={!!cfg.enabled} disabled={!canEdit}
                   onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="prc-setting-enabled" />
            Review enabled ({cfg.model}{cfg.llm_configured ? "" : " · no LLM key"})</label>
        </div>
        {canEdit && <Button size="sm" className="mt-3" onClick={save} disabled={busy} data-testid="prc-settings-save">Save</Button>}
      </div>
    </div>
  );
}
